import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './HoldingOverview.css';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

function HoldingOverview() {
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState(null);
  
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activePreset, setActivePreset] = useState('This year');

  // Chart specific filters
  const [chartCompanyFilter, setChartCompanyFilter] = useState('All');
  const [chartMonthFilter, setChartMonthFilter] = useState('All');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  
  const navigate = useNavigate();

  const loadDashboardData = (start = '', end = '') => {
    let queryString = '';
    if (start && end) {
      queryString = `?start_date=${start}&end_date=${end}`;
    }
    
    fetch(`${API}/companies/overview${queryString}`, { headers: getAuthHeaders() })
      .then((response) => {
        if (response.status === 401) {
          navigate('/login');
          throw new Error('Session expired - please log in again.');
        }
        if (response.status === 403) {
          throw new Error('Not authorized - please log in with a Holding-level account.');
        }
        if (!response.ok) throw new Error("Network response was not ok");
        return response.json();
      })
      .then((data) => {
        setSummary(data.summary);
        setCompanies(data.companies);
        setError(null);
      })
      .catch((err) => setError(err.message));
  };

  const handleDatePreset = (preset) => {
    setActivePreset(preset);
    const today = new Date();
    let start = '';
    let end = '';

    switch (preset) {
      case 'This month':
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
        break;
      case 'Prev month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().split('T')[0];
        end = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];
        break;
      case 'This quarter':
        const currentQ = Math.floor(today.getMonth() / 3);
        start = new Date(today.getFullYear(), currentQ * 3, 1).toISOString().split('T')[0];
        end = new Date(today.getFullYear(), currentQ * 3 + 3, 0).toISOString().split('T')[0];
        break;
      case 'Prev quarter':
        const prevQ = Math.floor(today.getMonth() / 3) - 1;
        start = new Date(today.getFullYear(), prevQ * 3, 1).toISOString().split('T')[0];
        end = new Date(today.getFullYear(), prevQ * 3 + 3, 0).toISOString().split('T')[0];
        break;
      case 'This year':
        start = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        end = new Date(today.getFullYear(), 11, 31).toISOString().split('T')[0];
        break;
      case 'Prev year':
        start = new Date(today.getFullYear() - 1, 0, 1).toISOString().split('T')[0];
        end = new Date(today.getFullYear() - 1, 11, 31).toISOString().split('T')[0];
        break;
      case 'All time':
        start = '';
        end = '';
        break;
      default:
        break;
    }

    setStartDate(start);
    setEndDate(end);
    if (preset !== 'Custom') {
      loadDashboardData(start, end);
    }
  };

  useEffect(() => {
    handleDatePreset('This year');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setChartCompanyFilter('All');
    setChartMonthFilter('All');
  }, [summary]);

  const handleApplyCustomFilter = () => {
    setActivePreset('Custom');
    if (startDate && endDate) {
      loadDashboardData(startDate, endDate);
    }
  };

  const openModal = (type, title) => {
    setModalType(type);
    setModalTitle(title);
    setIsModalOpen(true);
  };

  const calculateMargin = (net, gross) => {
    if (!gross || gross === 0) return 'Not Applicable';
    const margin = (net / gross) * 100;
    return `${margin.toFixed(1)}%`;
  };

  const getModalCompanies = () => {
    switch (modalType) {
      case 'winning': return companies.filter(c => c.net_profit >= 0);
      case 'losing': return companies.filter(c => c.net_profit < 0);
      default: return companies;
    }
  };

  const handleContinueSetup = (companyId) => {
    fetch(`${API}/companies/${companyId}/activate`, {
      method: 'POST',
      headers: { 'Accept': 'application/json' }
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Activation failed.');
        navigate(`/dashboard/${companyId}/overview`);
      })
      .catch(err => {
        alert(err.message + ' Use Edit to set a module and admin first, then try again.');
      });
  };

  if (error && error.includes('Not authorized')) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', padding: '40px', textAlign: 'center', backgroundColor: '#f8f9fa' }}>
      <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔒</div>
      <h2 style={{ color: '#1a1a2e', marginBottom: '10px', fontSize: '24px' }}>Access Restricted</h2>
      <p style={{ color: '#666', fontSize: '16px', marginBottom: '30px', maxWidth: '500px' }}>
        The Holding Overview dashboard requires Holding-level access (Super Admin or Holding Admin).
        Please use the sidebar to navigate to your assigned company.
      </p>
    </div>
  );
  if (error) return <div className="error">Error loading data: {error}</div>;
  if (!summary) return <div className="loading">Loading dashboard...</div>;

  let baseChartData = [];
  if (chartCompanyFilter === 'All') {
    baseChartData = summary?.monthly_chart_data || [];
  } else {
    const selectedCompany = companies.find(c => c.id.toString() === chartCompanyFilter);
    baseChartData = selectedCompany?.monthly_chart_data || [];
  }

  const filteredChartData = chartMonthFilter === 'All' 
    ? baseChartData 
    : baseChartData.filter(d => d.month === chartMonthFilter);

  return (
    <>
      <style>{`
        html, body {
          height: 100vh;
          margin: 0;
          padding: 0;
          overflow: hidden;
          box-sizing: border-box;
        }
        *, *:before, *:after {
          box-sizing: border-box;
        }
      `}</style>

      {/* SCROLLABLE VIEWPORT CONTAINER */}
      <div 
        className="dashboard-container" 
        style={{ 
          height: '100vh',
          overflowY: 'auto',
          padding: '0 24px 24px 24px', 
          margin: 0, 
          backgroundColor: '#f8f9fa', 
          flex: 1,
          width: '100%'
        }}
      >
        
        {/* STICKY FIXED HEADER */}
        <div 
          className="filter-section" 
          style={{ 
            position: 'sticky', 
            top: '0', 
            zIndex: 100, 
            backgroundColor: '#f8f9fa', 
            padding: '20px 0',
            margin: '0',
            borderBottom: '1px solid #e2e8f0', 
            marginBottom: '24px',
            display: 'flex',
            flexDirection: 'row',            
            alignItems: 'center',            
            justifyContent: 'space-between', 
            flexWrap: 'nowrap',              
            width: '100%',
            overflowX: 'auto', 
            overflowY: 'hidden',
            boxShadow: '0 4px 6px -4px rgba(0, 0, 0, 0.05)'
          }}
        >
          {/* Preset Buttons */}
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap', gap: '8px', margin: 0 }}>
            {['This month', 'Prev month', 'This quarter', 'Prev quarter', 'This year', 'Prev year', 'All time'].map(preset => (
              <button 
                key={preset}
                onClick={() => handleDatePreset(preset)}
                style={{
                  flexShrink: 0,                
                  height: '36px',                
                  padding: '0 12px',
                  borderRadius: '18px',
                  border: activePreset === preset ? 'none' : '1px solid #cbd5e1',
                  backgroundColor: activePreset === preset ? '#2563eb' : '#fff',
                  color: activePreset === preset ? '#fff' : '#64748b',
                  cursor: 'pointer',
                  fontWeight: activePreset === preset ? '600' : '400',
                  fontSize: '0.8rem',
                  whiteSpace: 'nowrap',
                  margin: 0
                }}
              >
                {preset}
              </button>
            ))}
          </div>

          {/* Custom Date Inputs */}
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px', flexWrap: 'nowrap', margin: 0 }}>
            <div style={{ flexShrink: 0, width: '1px', height: '24px', backgroundColor: '#cbd5e1', margin: '0 4px' }}></div>
            
            <span style={{ flexShrink: 0, fontSize: '0.75rem', color: '#64748b', fontWeight: '700', whiteSpace: 'nowrap', margin: 0 }}>START:</span>
            <input 
              type="date" 
              value={startDate} 
              onChange={(e) => { setStartDate(e.target.value); setActivePreset('Custom'); }} 
              style={{ flexShrink: 0, height: '36px', padding: '0 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem', outline: 'none', margin: 0 }}
            />

            <span style={{ flexShrink: 0, fontSize: '0.75rem', color: '#64748b', fontWeight: '700', whiteSpace: 'nowrap', margin: 0 }}>END:</span>
            <input 
              type="date" 
              value={endDate} 
              onChange={(e) => { setEndDate(e.target.value); setActivePreset('Custom'); }} 
              style={{ flexShrink: 0, height: '36px', padding: '0 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem', outline: 'none', margin: 0 }}
            />

            <button 
              onClick={handleApplyCustomFilter}
              style={{ flexShrink: 0, height: '36px', padding: '0 16px', backgroundColor: '#0f172a', color: '#fff', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem', whiteSpace: 'nowrap', margin: 0 }}
            >
              Apply
            </button>
          </div>
        </div>

        {/* SUMMARY GRID */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '20px', marginBottom: '30px', width: '100%' }}>
          <div className="summary-card" onClick={() => openModal('total', 'Companies Breakdown')}>
            <h3>Companies</h3>
            <p className="card-value">{summary.total_companies}</p>
          </div>
          <div className="summary-card" onClick={() => openModal('winning', 'Winning Companies Breakdown')}>
            <h3>Winning Companies</h3>
            <p className="card-value text-green">{summary.winning_companies}</p>
          </div>
          <div className="summary-card" onClick={() => openModal('losing', 'Losing Companies Breakdown')}>
            <h3>Losing Companies</h3>
            <p className="card-value text-red">{summary.losing_companies}</p>
          </div>
          <div className="summary-card" onClick={() => openModal('sales', 'Income Breakdown')}>
            <h3>Income</h3>
            <p className="card-value">${Number(summary.total_gross_sales).toLocaleString()}</p>
          </div>
          <div className="summary-card" onClick={() => openModal('expenses', 'Expenses Breakdown')}>
            <h3>Expenses</h3>
            <p className="card-value">${Number(summary.total_expenses).toLocaleString()}</p>
          </div>
          <div className="summary-card" onClick={() => openModal('profit', 'Net Cash Breakdown')}>
            <h3>Net Cash</h3>
            <p className={`card-value ${summary.total_net_profit >= 0 ? 'text-green' : 'text-red'}`}>
              ${Number(summary.total_net_profit).toLocaleString()}
            </p>
          </div>
          <div className="summary-card" onClick={() => openModal('employees', 'Employees Breakdown')}>
            <h3>Employees</h3>
            <p className="card-value">{summary.total_employees}</p>
          </div>
          <div className="summary-card" onClick={() => openModal('projects', 'Active Projects Breakdown')}>
            <h3>Active Projects</h3>
            <p className="card-value">{summary.total_active_projects}</p>
          </div>
        </div>

        {/* CHART SECTION */}
        <div className="chart-section" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
            <h2 className="section-title" style={{ margin: 0 }}>Financial Performance</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
              <select 
                value={chartCompanyFilter}
                onChange={(e) => {
                  setChartCompanyFilter(e.target.value);
                  setChartMonthFilter('All'); 
                }}
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#0f172a', fontSize: '0.85rem', fontWeight: '500', outline: 'none', cursor: 'pointer' }}
              >
                <option value="All">All Companies (Holding)</option>
                {companies.map(comp => (
                  <option key={comp.id} value={comp.id.toString()}>{comp.name}</option>
                ))}
              </select>

              <select 
                value={chartMonthFilter}
                onChange={(e) => setChartMonthFilter(e.target.value)}
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#0f172a', fontSize: '0.85rem', fontWeight: '500', outline: 'none', cursor: 'pointer' }}
              >
                <option value="All">All Months</option>
                {summary?.monthly_chart_data?.map(data => (
                  <option key={data.month} value={data.month}>{data.month}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="chart-container" style={{ width: '100%' }}>
            {filteredChartData && filteredChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={filteredChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#64748b' }} dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b' }} dx={-10} tickFormatter={(value) => `$${value / 1000}k`} />
                  <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Bar name="Income" dataKey="Sales" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={40} />
                  <Bar dataKey="Expenses" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={40} />
                  <Bar name="Net Cash" dataKey="NetProfit" radius={[4, 4, 0, 0]} barSize={40}>
                    {filteredChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.NetProfit >= 0 ? '#10b981' : '#dc2626'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="empty-state">No chart data available for this selection.</p>
            )}
          </div>
        </div>

        {/* COMPANY LIST SECTION */}
        <div className="company-list-section" style={{ width: '100%' }}>
          <h2 className="section-title">Company Performance</h2>
          <div className="company-list">
            {companies.map((company) => {
              const margin = calculateMargin(company.net_profit, company.gross_sales);
              const isMarginPositive = parseFloat(margin) >= 0;

              return (
                <div key={company.id} className="company-horizontal-card">
                  <div className="company-info">
                    <div className="logo-placeholder">Logo</div>
                    <div>
                      <h4><button type="button" onClick={() => navigate(`/dashboard/${company.id}/overview`)} style={{ background: 'none', border: 0, padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer', textAlign: 'left' }}>{company.name}</button></h4>
                      <span className={`status-badge ${company.status.replace(' ', '-').toLowerCase()}`}>
                        {company.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="company-metrics-grid">
                    <div className="metric">
                      <span className="metric-label">Income</span>
                      <span className="metric-value">${Number(company.gross_sales).toLocaleString()}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Expenses</span>
                      <span className="metric-value">${Number(company.expenses).toLocaleString()}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Net Cash</span>
                      <span className={`metric-value ${company.net_profit >= 0 ? 'text-green' : 'text-red'}`}>
                        ${Number(company.net_profit).toLocaleString()}
                      </span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Cash Margin</span>
                      <span className={`metric-value ${margin === 'Not Applicable' ? '' : (isMarginPositive ? 'text-green' : 'text-red')}`}>
                        {margin}
                      </span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Employees</span>
                      <span className="metric-value">{company.employee_count}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Active Projects</span>
                      <span className="metric-value">{company.active_projects}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Receivables</span>
                      <span className="metric-value">${Number(company.outstanding_receivables || 0).toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="company-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {company.status === 'Active' ? (
                      <button 
                        onClick={() => navigate(`/dashboard/${company.id}/overview`)}
                        style={{ width: '110px', height: '36px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap' }}
                      >
                        Dashboard
                      </button>
                    ) : (
                      <button 
                        onClick={() => handleContinueSetup(company.id)}
                        style={{ width: '110px', height: '36px', backgroundColor: '#eab308', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap' }}
                      >
                        Continue Setup
                      </button>
                    )}

                    <button 
                      onClick={() => navigate(`/companies/edit/${company.id}`)}
                      style={{ width: '110px', height: '36px', backgroundColor: '#0f172a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap' }}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {isModalOpen && (
          <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{modalTitle}</h2>
                <button className="btn-close" onClick={() => setIsModalOpen(false)}>×</button>
              </div>
              <div className="modal-body">
                <p className="modal-period">Period: {startDate || 'All Time'} to {endDate || 'All Time'}</p>
                
                <div className="modal-company-list">
                  {getModalCompanies().length > 0 ? (
                    getModalCompanies().map(company => {
                      const salesContribution = summary.total_gross_sales > 0 
                        ? ((company.gross_sales / summary.total_gross_sales) * 100).toFixed(1) 
                        : 0;
                      const expensesContribution = summary.total_expenses > 0 
                        ? ((company.expenses / summary.total_expenses) * 100).toFixed(1) 
                        : 0;
                      const employeeContribution = summary.total_employees > 0
                        ? ((company.employee_count / summary.total_employees) * 100).toFixed(1)
                        : 0;

                      return (
                        <div key={company.id} className="modal-company-row" onClick={() => navigate(`/dashboard/${company.id}/overview`)}>
                          <div className="modal-company-info" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div className="logo-placeholder" style={{ width: '32px', height: '32px', fontSize: '0.65rem', flexShrink: 0 }}>Logo</div>
                            <div>
                              <span className="modal-company-name">{company.name}</span>
                              <br />
                              <span className="modal-metric-detail">
                                {modalType === 'sales' && `Sales: $${Number(company.gross_sales).toLocaleString()} (${salesContribution}%)`}
                                {modalType === 'expenses' && `Expenses: $${Number(company.expenses).toLocaleString()} (${expensesContribution}%)`}
                                {(modalType === 'winning' || modalType === 'losing' || modalType === 'profit') && `Income: $${Number(company.gross_sales).toLocaleString()} · Expenses: $${Number(company.expenses).toLocaleString()} · Net Cash: $${Number(company.net_profit).toLocaleString()}`}
                                {modalType === 'employees' && `Employees: ${company.employee_count} (${employeeContribution}%)`}
                                {modalType === 'projects' && `Active Projects/Contracts: ${company.active_projects}`}
                                {modalType === 'total' && `Status: ${company.status}`}
                              </span>
                            </div>
                          </div>
                          <span className="modal-company-link">Open Dashboard →</span>
                        </div>
                      );
                    })
                  ) : (
                    <p className="empty-state">No companies found for this category in the selected period.</p>
                  )}
                </div>

                {getModalCompanies().length > 0 && (
                  <div className="modal-total-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '12px', borderTop: '2px solid #e2e8f0', fontWeight: '700', color: '#0f172a' }}>
                    <span>Holding Total</span>
                    <span>
                      {modalType === 'sales' && `$${Number(summary.total_gross_sales).toLocaleString()}`}
                      {modalType === 'expenses' && `$${Number(summary.total_expenses).toLocaleString()}`}
                      {modalType === 'profit' && `$${Number(summary.total_net_profit).toLocaleString()}`}
                      {modalType === 'employees' && `${summary.total_employees}`}
                      {modalType === 'projects' && `${summary.total_active_projects}`}
                      {modalType === 'total' && `${summary.total_companies} companies`}
                      {modalType === 'winning' && `${summary.winning_companies} companies`}
                      {modalType === 'losing' && `${summary.losing_companies} companies`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}

export default HoldingOverview;
